import Grid, { GridSize } from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useFlags } from "launchdarkly-react-client-sdk";
import { kebabCase } from "lodash";

import { useAppSelector } from "app/hooks";
import { SPEECH_WORD_RECOGNITION_ANNOTATIONS_BELOW_TABLES_FEATURE } from "constants/feature-flags";
import { selectCurrentUser } from "features/auth/auth-slice";
import { SpeechAudiometryReport } from "services/types/reports.type";
import { useGetUserQuery } from "services/users";

import { PreviewSpeechAudiometryDataGroup } from "./components";

interface PreviewSpeechWordRecognitionSpeechAudiometryProps {
  title: string;
  data?: SpeechAudiometryReport | null;
  detailSize?: GridSize;
  wordListSize?: GridSize;
  titleSize?: GridSize;
  pb?: number;
  titleMb?: number;
}

export default function PreviewSpeechWordRecognitionSpeechAudiometry({
  data,
  title,
  detailSize,
  wordListSize,
  titleSize,
  pb = 5,
  titleMb = 3
}: PreviewSpeechWordRecognitionSpeechAudiometryProps) {
  const { palette } = useTheme();
  const flags = useFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const { data: user } = useGetUserQuery(currentUser.id, {
    skip: !currentUser.id || currentUser.id === -1
  });

  const switchAudiogramOrientation = Boolean(
    user?.user_preferences?.reporting?.switch_audiogram_orientation
  );
  // SN-20073: with the flag on the note follows the ear values, as it does in the PDF.
  const annotationsBelowTables = Boolean(
    flags[SPEECH_WORD_RECOGNITION_ANNOTATIONS_BELOW_TABLES_FEATURE]
  );
  const annotation = Boolean(data?.annotation) && (
    <Grid
      item
      container
      lg={12}
      sx={annotationsBelowTables ? { mt: 2 } : { mb: 2 }}
    >
      <Typography
        variant="body2"
        fontStyle="italic"
        data-testid={`${kebabCase(title)}-annotation`}
      >
        {data?.annotation}
      </Typography>
    </Grid>
  );

  return (
    <Grid
      item
      container
      spacing={0}
      marginTop={1}
      sx={{ pb }}
      data-testid={`subgroup-${kebabCase(title)}`}
    >
      {title && (
        <Grid item container lg={12} sx={{ mb: titleMb }}>
          <Typography
            fontSize={24}
            lineHeight="28px"
            color={palette.previewSpeechWordRecognition.color}
          >
            {title}
          </Typography>
        </Grid>
      )}
      {!annotationsBelowTables && annotation}
      {switchAudiogramOrientation ? (
        <PreviewSpeechAudiometryDataGroup
          data={data?.rightEar}
          title="Right"
          detailSize={detailSize}
          wordListSize={wordListSize}
          titleSize={titleSize}
        />
      ) : (
        <PreviewSpeechAudiometryDataGroup
          data={data?.leftEar}
          title="Left"
          detailSize={detailSize}
          wordListSize={wordListSize}
          titleSize={titleSize}
        />
      )}

      <PreviewSpeechAudiometryDataGroup
        data={data?.binaural}
        title="Binaural"
        detailSize={detailSize}
        wordListSize={wordListSize}
        titleSize={titleSize}
      />
      {switchAudiogramOrientation ? (
        <PreviewSpeechAudiometryDataGroup
          data={data?.leftEar}
          title="Left"
          detailSize={detailSize}
          wordListSize={wordListSize}
          titleSize={titleSize}
        />
      ) : (
        <PreviewSpeechAudiometryDataGroup
          data={data?.rightEar}
          title="Right"
          detailSize={detailSize}
          wordListSize={wordListSize}
          titleSize={titleSize}
        />
      )}
      {annotationsBelowTables && annotation}
    </Grid>
  );
}
